import { ProductMetafields } from '../../api/shopify/types';
import { ShopifyProduct } from '@jbhi-fi/components/src/Shopify/types';
import { ShopifyProductTransformer } from '@jbhi-fi/components/src/Shopify/transformers/ShopifyProductTransformer';
import { filterAndMapAttributes } from '@jbhi-fi/apis/src/utils/filterAndMapAttributes';
import { ShopifyStorefrontClient } from '../../api/shopify/ShopifyStorefrontClient';
import { JbCart, JbCartItem } from '@jbhi-fi/apis/src/shopify/types/JbCart';

const storefrontClient = new ShopifyStorefrontClient();

export const refreshTieredProductDetails = async (cart: JbCart | undefined) => {
  if (!cart) return;
  const changedProducts = cart.items
    .filter(({ properties }) => properties._marketplaceTieredId)
    .map(async item => {
      const jsonResponse = await getProductData(item.handle);
      if (!jsonResponse) return;

      const { product, metafields } = jsonResponse;
      const { variants } = jsonResponse.product;
      const { marketplaceSellers } = metafields;
      const { value: onlineProduct } = jsonResponse.metafields.online_product;

      const seller = ShopifyProductTransformer.toMarketplaceSeller({ product, marketplaceSellers, onlineProduct });

      if (seller && item.properties._marketplaceTieredId !== seller.tieredIds[0]) {
        const hasSellerChanged = item.properties._marketplaceSeller !== seller.name;
        return {
          item,
          seller,
          replacementVariantId: hasSellerChanged
            ? variants.find(variant => variant.option1 === seller.name)?.id
            : undefined,
        };
      }
    });

  const changeRequests = (await Promise.all(changedProducts)).filter(req => !!req);
  if (changeRequests.length === 0) return cart;
  let updatedCart;
  for (const change of changeRequests) {
    if (!change) continue;
    const { item, seller, replacementVariantId } = change;
    if (replacementVariantId) {
      updatedCart = await storefrontClient.removeFromCart(item.line_id);
      if (updatedCart) {
        updatedCart = await storefrontClient.addToCart(
          replacementVariantId,
          item.quantity,
          filterAndMapAttributes({
            ...item.properties,
            ...getMarketplacePropertiesBySeller(seller, true),
          })
        );
      }
    } else {
      updatedCart = await storefrontClient.updateCartLine(
        item.line_id,
        item.quantity,
        filterAndMapAttributes({
          ...item.properties,
          ...getMarketplacePropertiesBySeller(seller, true),
        })
      );
    }
  }

  return updatedCart;
};

export const getMarketplacePropertiesFromExistingItem = (item: JbCartItem) => {
  return {
    _marketplaceSeller: item.properties._marketplaceSeller,
    _marketplaceSellerId: item.properties._marketplaceSellerId,
    _marketplaceTieredId: item.properties._marketplaceTieredId,
    _marketplaceSellerIsDigitalTaxInvoiceProvided: item.properties._marketplaceSellerIsDigitalTaxInvoiceProvided,
    _marketplaceSellerIdAtc: item.properties._marketplaceSellerIdAtc,
    _marketplaceTieredIdAtc: item.properties._marketplaceTieredIdAtc,
  };
};

export const getMarketplacePropertiesBySeller = (
  seller:
    | {
        name: string;
        id: string;
        isDigitalInvoiceProvided?: boolean | null;
        tieredIds: Array<string>;
      }
    | undefined,
  isRefresh: boolean = false
) => {
  if (!seller) return {};

  const baseProperties = {
    _marketplaceSeller: seller.name ?? '',
    _marketplaceSellerId: seller.id.toString() ?? '',
    _marketplaceTieredId: seller.tieredIds[0],
    _marketplaceSellerIsDigitalTaxInvoiceProvided: seller.isDigitalInvoiceProvided?.toString() ?? 'false',
  };

  if (!isRefresh) {
    return {
      ...baseProperties,
      _marketplaceSellerIdAtc: seller.id.toString() ?? '',
      _marketplaceTieredIdAtc: seller.tieredIds[0],
    };
  }

  return baseProperties;
};

const getProductData = async (
  handle: string
): Promise<
  | {
      product: ShopifyProduct;
      metafields: ProductMetafields;
    }
  | undefined
> => {
  try {
    const jsonResponse = await fetch(`/products/${handle}?view=json`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return jsonResponse.json();
  } catch (error) {
    console.error('Error while getting product data: ', error);
  }
};
